import * as Sentry from "@sentry/react";
import type { CallStateAggregate } from "@theirstory/common/call_state/types";
import AudioRecorder from "audio-recorder-polyfill";
import axios from "axios";
import { nanoid } from "nanoid";
import type { Middleware } from "redux";

import { notifySuccess } from "../../actions/notify";
import {
  AUTH_USER,
  CALL_CHECK_SCREEN_SHARE,
  CALL_DISCONNECTED,
  CALL_FAILED,
  CALL_FINISH,
  CALL_FLUSH_LOCAL_RECORDING,
  CALL_LOCAL_RECORDING_UPLOAD_COMPLETION,
  CALL_LOCAL_RECORDING_UPLOAD_FAILED,
  CALL_LOCAL_RECORDING_UPLOAD_PENDING,
  CALL_LOCAL_RECORDING_UPLOAD_START,
  CALL_MUTE_ALL,
  CALL_PARTICIPANT_IDENTIFIED,
  CALL_PEER_JOINED,
  CALL_PEER_LEFT,
  CALL_PEER_SPEAKING,
  CALL_PUBLISH_SUCCESS,
  CALL_RECEIVED_CHAT_HISTORY_MESSAGE,
  CALL_RECEIVED_INFO_MESSAGE,
  CALL_RECEIVED_MESSAGE,
  CALL_REQUEST_PEER_INFO,
  CALL_SCREEN_SHARE_CAP,
  CALL_SCREEN_SHARE_EXT,
  CALL_SCREEN_SHARING,
  CALL_SEND_CHAT_HISTORY_MESSAGE,
  CALL_SEND_INFO_MESSAGE,
  CALL_SEND_MESSAGE,
  CALL_SEND_PEER_INFO,
  CALL_SENT_MESSAGE,
  CALL_SET_PEER_FRAME_RATE,
  CALL_SET_PEER_RESOLUTION,
  CALL_SETTINGS_PUBLISH,
  CALL_SETTINGS_PUBLISH_FAILURE,
  CALL_SETTINGS_PUBLISH_SUCCESS,
  CALL_SETTINGS_SET_DEVICES_SUCCESS,
  CALL_SETTINGS_SWITCH_DEVICE,
  CALL_SETTINGS_UNPUBLISH_SUCCESS,
  CALL_SETTINGS_UPDATE_AUDIO_VIDEO,
  CALL_INIT,
  CALL_START_SCREEN_SHARING,
  CALL_START_SETTINGS,
  CALL_STARTED,
  CALL_STARTED_RECORDING,
  CALL_STOP_SCREEN_SHARING,
  CALL_STOP_SETTINGS,
  CALL_STOPPED_RECORDING,
  CALL_SWITCH_DEVICE,
  CALL_TOGGLE_CAMERA,
  CALL_TOGGLE_LOCAL_RECORDING,
  CALL_TOGGLE_MICROPHONE,
  CALL_TOGGLE_PEER_AUDIO,
  CALL_TOGGLE_PEER_VIDEO,
  CALL_STOP_LOCAL_RECORDING,
  CALL_PARTICIPANT_STARTED_LOCALLY_RECORDING,
  CALL_LOCAL_RECORDING_CAPABILITY_DETERMINED,
} from "../../actions/types";
import { buildLoggerTags, pushGtm } from "../../../components/utils/util";
import { recordingsDB } from "../../../lib/localdb";
import type { LocalAudioStream } from "../../../lib/localRecording";
import logger from "../../../lib/logger";
import { getLocalMediaRecordingInstance } from "../local_recording/utils";
import type { RootState } from "../rootReducer";
import type { DispatchTypeHelper } from "../rootStore";

import { isOnline, STATUS_CONNECTING } from "./reducer";
import type { LocalRecordingInfo } from "./reducer";
import {
  broadcastParticipantUpdate,
  completeLocalRecordingUpload,
  // failedLocalRecordingUpload,
  startLocalRecordingUpload,
  updateParticipantsLocal,
} from "./actions";

const logHeaders = logger.headers;

declare let OT: any;

function startSpeakerDetection(subscriber, startTalking) {
  let activity = null;
  subscriber.on("audioLevelUpdated", (event) => {
    const now = Date.now();
    if (event.audioLevel > 0.2) {
      if (!activity) {
        activity = { timestamp: now, talking: false };
      } else if (activity.talking) {
        activity.timestamp = now;
      } else if (now - activity.timestamp > 500) {
        activity.talking = true;
        if (typeof startTalking === "function") {
          startTalking();
        }
      }
    } else if (activity && now - activity.timestamp > 1000) {
      activity = null;
    }
  });
}

function isChrome() {
  return navigator.userAgent.indexOf("Chrome") !== -1;
}

export interface CallContext {
  storyContext: any;
  session: any;
  publisher: any;
  subscribers: Map<string, any>;
  localMediaRecorder: MediaRecorder;
  localStreamId: string;
  localRecordingId: string;
  localRecordingCapable: boolean;
  localRecordingCapability: CallStateAggregate["participants"][0]["localRecording"]["capability"];
  localTimeOffset: number;
  screenShare: any;
}

const RETRY_BACKOFF_TIMES = [500, 1000, 2000, 4000];

export default (): Middleware<never, RootState, DispatchTypeHelper> =>
  ({ dispatch, getState }) => {
    const callCtx: CallContext = {
      storyContext: null,
      session: null,
      publisher: null,
      subscribers: new Map(),
      localMediaRecorder: null,
      localStreamId: null,
      localRecordingId: null,
      localRecordingCapable: false,
      localRecordingCapability: "UNKNOWN",
      localTimeOffset: null,
      screenShare: null,
    };
    const settingsCtx = {
      publisher: null,
    };

    const buildTags = () => buildLoggerTags(getState(), callCtx.storyContext.storyId);

    function reportError(header, error, retryCount = null) {
      logger.error(header, { error: error, ...buildTags() });
      console.error(header, error);

      // This function gets called from some places where we want to report an error
      // every time. In other places, it is part of a retry loop, in which case retryCount
      // will be a number. When we are in the loop, let's only report one event to Sentry
      //
      // TODO: For now leaving this at 0 so that we can monitor how this code behaves in the wild
      // as soon as it is hit. As we gain confidence (next 4-6 weeks?), let's change this to a 1
      // or 2 so that if the client auto-recovers after first retry, we do not generate extra
      // noise in Sentry
      // -- DXM 2022-01-11
      if (retryCount == null || retryCount === 0) {
        Sentry.captureException(error);
      }
    }

    function trace(msg, extra = null) {
      logger.info(logHeaders.SESSION_TRACE, { msg, ...buildTags(), ...(extra ? { extra } : {}) });
      console.log(msg);
    }

    function gtmTrace(callAction) {
      pushGtm({
        story_id: callCtx.storyContext.storyId,
        event: "call_action",
        call_action: callAction,
      });
    }

    function broadcastParticipantEntry() {
      const {
        call: { participants },
        auth: { visitorId, full_name: authName },
        localRecording: { pending: pendingLocalUploads },
      } = getState();
      const myInfo = participants.get(visitorId);
      const nameProp = authName ? { name: authName } : myInfo && myInfo.name ? { name: myInfo.name } : {};
      const localRecordings = {};

      pendingLocalUploads.forEach((stream) => {
        if (stream.storyId === callCtx.storyContext.storyId) {
          localRecordings[stream.id] = {
            localRecordingStarted: true,
            localRecordingStopped: true,
          };
        }
      });

      dispatch(
        broadcastParticipantUpdate(visitorId, {
          ...nameProp,
          role: callCtx.storyContext.creator ? "host" : "guest",
          localRecordingCapable: callCtx.localRecordingCapable,
          localRecordings,
        })
      );
    }

    function scheduleRetry(retryCount: number, cb: () => void): ReturnType<typeof setTimeout> {
      const ms = RETRY_BACKOFF_TIMES[retryCount] || RETRY_BACKOFF_TIMES[RETRY_BACKOFF_TIMES.length - 1];
      return setTimeout(cb, ms);
    }

    function createSettingsPublisher(audioSource, videoSource) {
      const publisherId = nanoid(8);
      gtmTrace("call_publisher_init");
      const publisher = OT.initPublisher(
        null,
        {
          insertDefaultUI: false,
          audioSource,
          videoSource,
        },
        (error) => {
          trace(`CALL::Publisher(${publisherId}) - INIT ${error ? "FAIL" : "SUCCESS"}`);
          if (error) {
            dispatch({ type: CALL_SETTINGS_PUBLISH_FAILURE, error: error.message });
          }
        }
      );
      publisher.shortId = publisherId;
      settingsCtx.publisher = publisher;
      trace(`CALL::Publisher(${publisher.shortId}) - CREATED - for settings`);
      gtmTrace("call_publisher_created");

      publisher.on("videoElementCreated", (evt) => {
        trace(`CALL::Publisher(${publisher.shortId}) - VideoElementCreated`);
        setTimeout(() => {
          dispatch({
            type: CALL_SETTINGS_PUBLISH_SUCCESS,
            settingsPublisher: evt.element,
          });
        }, 0);
      });
    }

    function initializeSettings(skipPublisher: boolean) {
      try {
        if (callCtx.publisher) {
          // I will buy a drink (or a milkshake) to anyone who figures out the value of having
          // this code snippet here and how/why it actually makes a difference
          // -- DXM 2021-04-06

          // This will only be initialized with an audio source right after publisher fires
          // 'videoElementCreated' event and right before OT.initPublisher() callback is invoked
          const audioStream = callCtx.publisher.getAudioSource();
          if (audioStream) {
            const newAudioTrack = audioStream.clone();
            callCtx.publisher.setAudioSource(newAudioTrack);
          }
        }

        navigator.mediaDevices
          .enumerateDevices()
          .then((devices) => {
            const audioDevices = devices.filter((device) => device.kind === "audioinput");
            const videoDevices = devices.filter((device) => device.kind === "videoinput");
            const outputDevices = devices.filter((device) => device.kind === "audiooutput");

            const audioSource = audioDevices[0] && audioDevices[0].deviceId;
            const videoSource = videoDevices[0] && videoDevices[0].deviceId;
            const audioOutput = outputDevices[0] && outputDevices[0].deviceId;

            dispatch({
              type: CALL_SETTINGS_SET_DEVICES_SUCCESS,
              audioDevices,
              videoDevices,
              outputDevices,
            });

            if (!skipPublisher) {
              dispatch({
                type: CALL_SETTINGS_PUBLISH,
                audioSource,
                videoSource,
                audioOutput,
              });
            }
          })
          .catch(Sentry.captureException);
      } catch (e) {
        console.error("Error while initializing settings!", e);
        Sentry.captureException(e);
        dispatch({
          type: CALL_SETTINGS_PUBLISH_FAILURE,
          error: "Unable to find or initialize devices. Please refresh and verify permissions.",
        });
      }
    }

    function stopCallSettings() {
      dispatch({
        type: CALL_SETTINGS_SET_DEVICES_SUCCESS,
        audioDevices: [],
        videoDevices: [],
        outputDevices: [],
      });

      if (settingsCtx.publisher) {
        trace(`CALL::Publisher(${settingsCtx.publisher.shortId}) - Destroying...`);
        gtmTrace("call_publisher_destroy");
        settingsCtx.publisher.destroy();
        settingsCtx.publisher = null;

        dispatch({ type: CALL_SETTINGS_UNPUBLISH_SUCCESS, settingsPublisher: null });
      }
    }

    function switchSettingsDevice(audioSource, videoSource) {
      if (!settingsCtx.publisher) return;

      settingsCtx.publisher.setVideoSource(videoSource);
      settingsCtx.publisher.setAudioSource(audioSource);
    }

    async function canStartLocalRecording() {
      const { call } = getState();
      if (!callCtx.localStreamId || callCtx.localMediaRecorder || !recordingsDB || !callCtx.localRecordingCapable) {
        return false;
      } else if (callCtx.storyContext.localRecording || call.localRecording) {
        return true;
      } else {
        const response = await axios.get(`/stories/${callCtx.storyContext.storyId}/check_recording`);
        return !!response.data.local_recording;
      }
    }

    async function initMediaRecorder() {
      const callFlags = getState().call.featureFlags;
      const { localRecordingMimeType, localRecordingVideoBitrate, localRecordingMaxResolution } = callFlags || {};
      const localRecordingVideo = localRecordingMimeType?.startsWith("video");

      // see options https://tokbox.com/developer/sdks/js/reference/OT.html#getUserMedia
      // do check MediaRecorder && MediaRecorder.isTypeSupported('audio/webm;codecs=pcm')
      const { stream: mediaStream } = isChrome()
        ? await getChromeSources(localRecordingVideo, localRecordingMaxResolution)
        : {
            stream: await OT.getUserMedia({
              videoSource: localRecordingVideo ? getState().call.videoSource : false,
              audioSource: getState().call.audioSource,
            }),
          };

      // see options https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder/MediaRecorder
      // mimeType: 'audio/webm;codecs=pcm' for Chrome to record raw WAV
      // const mimeType = "video/webm;codecs=vp8,opus";
      // const mimeType = localRecordingVideo ? "video/webm;codecs=vp8,opus" : "audio/webm;codecs=opus";
      const mimeType = localRecordingMimeType || "audio/webm;codecs=opus";
      const recorderOpts = MediaRecorder.isTypeSupported(mimeType)
        ? {
            audioBitsPerSecond: 48000,
            videoBitsPerSecond: localRecordingVideoBitrate || 4000000,
            mimeType: mimeType,
          }
        : {}; // no opus support, use defaults (necessary with polyfill)

      return new MediaRecorder(mediaStream, recorderOpts);
    }

    async function startLocalRecording() {
      gtmTrace("call_local_recording_starting");
      try {
        const canStart = await canStartLocalRecording();
        if (!canStart) {
          gtmTrace("call_local_recording_cannot_start");
          return;
        }

        const mediaRecorder = await initMediaRecorder();

        mediaRecorder.stream.getAudioTracks().forEach((track) => {
          track.enabled = getState().call.publishing.audio;
        });
        mediaRecorder.stream.getVideoTracks().forEach((track) => {
          track.enabled = getState().call.publishing.video;
        });

        const isStreaming = getState().call.featureFlags?.localRecordingStreaming;

        const localMediaStorage = getLocalMediaRecordingInstance(isStreaming);

        const videoSettings = mediaRecorder.stream.getVideoTracks()?.[0]?.getSettings();
        const audioSettings = mediaRecorder.stream.getAudioTracks()?.[0]?.getSettings();

        const streamStorage = await localMediaStorage.createStream({
          streamId: callCtx.localStreamId,
          storyId: callCtx.storyContext.storyId,
          sessionId: callCtx.storyContext.sessionId,
          localTimeOffset: callCtx.localTimeOffset,
          userId: getState().auth.user_id,
          type: mediaRecorder.mimeType,
        });

        callCtx.localRecordingId = streamStorage.id;

        const broadcastLocalRecordingUpdate = (
          stage: string,
          value: boolean | number | { [key: string]: any } = true
        ) => dispatch(broadcastParticipantUpdate(getState().auth.visitorId, streamStorage.id, { [stage]: value }));

        mediaRecorder.addEventListener("dataavailable", (event) => {
          streamStorage.pushChunk(event).catch((err) => {
            reportError(logHeaders.LOCAL_REC_ERROR, err);
          });
        });

        mediaRecorder.addEventListener("start", (event: any) => {
          trace(
            `CALL::LocalRecording STARTED using ${
              event.target && event.target.mimeType ? event.target.mimeType : "unknown type"
            } - Id(${streamStorage.id} StreamId(${streamStorage.streamId})`
          );
          gtmTrace("call_local_recording_started");
          broadcastLocalRecordingUpdate("localRecordingStarted");
        });

        mediaRecorder.addEventListener("stop", () => {
          trace(
            `CALL::LocalRecording STOPPED; Id(${streamStorage.id} StreamId(${streamStorage.streamId}) uploading...`
          );
          gtmTrace("call_local_recording_stopped");
          broadcastLocalRecordingUpdate("localRecordingStopped");
          dispatch(
            notifySuccess(
              isStreaming ? "Finishing up your local media recording" : "Your locally recorded media is uploading."
            )
          );
          dispatch(startLocalRecordingUpload(streamStorage));

          if (isStreaming) {
            streamStorage.finish();
          } else {
            streamStorage
              .finish(true)
              .then(() => streamStorage.delete())
              .then(() => {
                dispatch(completeLocalRecordingUpload(streamStorage));
                broadcastLocalRecordingUpdate("localRecordingUploadSucceeded");
              })
              .catch((err) => {
                // dispatch(failedLocalRecordingUpload(streamStorage));
                broadcastLocalRecordingUpdate("localRecordingUploadFailed");
                reportError(logHeaders.LOCAL_REC_UPLOAD_ERROR, err);
              });
          }
        });

        mediaRecorder.addEventListener("error", (event) => {
          reportError(logHeaders.LOCAL_REC_ERROR, event);
        });
        mediaRecorder.addEventListener("onerror", (event) => {
          reportError(logHeaders.LOCAL_REC_ERROR, event);
        });

        // record into 30s array buffer blobs
        // when the dataavailable event fires, its data will be for this time slice only
        mediaRecorder.start(5_000);
        callCtx.localMediaRecorder = mediaRecorder;

        // Need to do this to get the label
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevice = devices.find((dev) => dev.deviceId === videoSettings?.deviceId);
        const audioDevice = devices.find((dev) => dev.deviceId === audioSettings?.deviceId);
        dispatch({
          type: CALL_PARTICIPANT_STARTED_LOCALLY_RECORDING,
          data: {
            recordingId: callCtx.localRecordingId,
            videoSettings,
            audioSettings,
            videoLabel: videoDevice?.label,
            audioLabel: audioDevice?.label,
            videoBitrate: mediaRecorder.videoBitsPerSecond,
            audioBitrate: mediaRecorder.audioBitsPerSecond,
          },
        });
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
      }
    }

    function stopLocalRecording() {
      gtmTrace("call_local_recording_stopping");
      if (!callCtx.localMediaRecorder) return;

      try {
        callCtx.localMediaRecorder.stop();
        callCtx.localMediaRecorder.stream.getTracks().forEach((track) => track.stop());
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
      }

      dispatch({ type: CALL_STOP_LOCAL_RECORDING, data: { recordingId: callCtx.localRecordingId } });
      callCtx.localMediaRecorder = null;
      callCtx.localRecordingId = null;
    }

    function flushLocalRecording() {
      // This function will get invoked when the user is forcibly reloading/refreshing the page.
      // We take this opportunity to stop the local recording and then we dispatch CALL_FINISH
      // to signal termination of the call (the client waiting on flush completion will resume
      // when call state is no longer recording)
      stopLocalRecording();
      dispatch({ type: CALL_FINISH });
    }

    function getCallPublisherOpts() {
      const featureFlagOpts = callCtx.storyContext.featureFlags?.callPublisher;
      return {
        autoGainControl: featureFlagOpts?.autoGainControl ?? true,
        disableAudioProcessing: featureFlagOpts?.disableAudioProcessing ?? false,
        echoCancellation: featureFlagOpts?.echoCancellation ?? true,
        enableStereo: featureFlagOpts?.enableStereo ?? false,
        noiseSuppression: featureFlagOpts?.noiseSuppression ?? true,
      };
    }

    function getAudioSourceConstraints() {
      const { call } = getState();
      const opts = getCallPublisherOpts();
      const constraints: MediaTrackConstraints = { deviceId: { exact: call.audioSource } };

      if (opts.enableStereo) {
        constraints.channelCount = 2;
      }

      ["echoCancellation", "autoGainControl", "noiseSuppression"].forEach((propName) => {
        constraints[propName] = !opts.disableAudioProcessing && opts[propName];
      });

      return constraints;
    }

    async function getChromeSources(
      videoEnabled = true,
      maxResolution: { width: number; height: number } = { width: 1920, height: 1080 }
    ) {
      const { call } = getState();

      const stream = await window.navigator.mediaDevices.getUserMedia({
        video: videoEnabled
          ? {
              deviceId: { exact: call.videoSource },
              width: { ideal: maxResolution.width },
              height: { ideal: maxResolution.height },
            }
          : false,
        audio: getAudioSourceConstraints(),
      });

      return {
        stream,
        videoSource: videoEnabled ? stream.getVideoTracks()[0] : null,
        audioSource: stream.getAudioTracks()[0],
      };
    }

    async function initCallPublisher(retryCount = 0) {
      gtmTrace("call_publisher_init");
      const publisherId = nanoid(8);
      const featureFlagOpts = getCallPublisherOpts();
      const { call } = getState();

      const { audioSource, videoSource } = isChrome()
        ? await getChromeSources()
        : {
            audioSource: call.audioSource,
            videoSource: call.videoSource,
          };

      // Extra boolean magic here is because initPublisher() may invoke its callback before
      // it returns and therefore before callCtx.publisher has been set. This will (hopefully)
      // cover that case
      let insideInitPublisher = true;
      const isAlive = () =>
        isOnline(getState().call.status) && (insideInitPublisher || callCtx?.publisher?.shortId === publisherId);

      // Call may have been terminated while we were awaiting on getChromeSources() above. Check that
      // we are still alive before attempting to init a publisher
      if (!isAlive()) return;

      const publisher = OT.initPublisher(
        null,
        {
          insertDefaultUI: false,
          name: "face",
          resolution: "1280x720",
          frameRate: 30,
          audioBitrate: 48000,
          audioFallbackEnabled: false,
          audioSource: audioSource,
          videoSource: videoSource,
          ...featureFlagOpts,
        },
        (err) => {
          if (!isAlive()) return;

          trace(`CALL::Publisher(${publisherId}) - INIT ${err ? "FAIL" : "SUCCESS"}`);
          if (err) {
            reportError(logHeaders.INIT_PUBLISH_ERROR, err, retryCount);
            scheduleRetry(retryCount, () => {
              if (isAlive()) {
                destroyPublisher();
                initCallPublisher(retryCount + 1);
              }
            });
          }
        }
      );

      insideInitPublisher = false;
      publisher.shortId = publisherId;
      callCtx.publisher = publisher;
      trace(`CALL::Publisher(${publisher.shortId}) - CREATED - featureOpts: ${JSON.stringify(featureFlagOpts)}`);

      publisher.publishAudio(call.publishing.audio);
      publisher.publishVideo(call.publishing.video);

      publisher.on("streamCreated", (evt) => {
        callCtx.localStreamId = evt.stream.streamId;
        trace(`CALL::Publisher(${publisher.shortId}) - streamCreated(${callCtx.localStreamId})`);
        const {
          call: { recording, localRecordingsPolyfill },
        } = getState();
        callCtx.localRecordingCapable =
          typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported("audio/webm;codecs=opus");
        if (!callCtx.localRecordingCapable && localRecordingsPolyfill) {
          (window as any).MediaRecorder = AudioRecorder;
          callCtx.localRecordingCapable = (MediaRecorder as any).notSupported === false;
        }
        broadcastParticipantEntry();
        dispatch({ type: CALL_REQUEST_PEER_INFO });

        if (!recordingsDB) {
          callCtx.localRecordingCapability = "IDB_UNAVAILABLE";
        } else if (!callCtx.localRecordingCapable) {
          callCtx.localRecordingCapability = "MEDIA_RECORDER_UNAVAILABLE";
        } else {
          callCtx.localRecordingCapability = "CAPABLE";
        }

        if (recording) {
          void startLocalRecording();
        }
      });

      publisher.on("streamDestroyed", (evt) => {
        trace(`CALL::Publisher(${publisher.shortId}) - streamDestroyed(${evt.stream?.streamId}) - ${evt.reason}`);
        if (isOnline(getState().call.status)) {
          if (isAlive()) {
            // Current theory is that there are 3 different reasons for us entering this code:
            //  1. User closing the browser tab caused network connections to stop while JS code is
            //     continuing to run for a bit (maybe?)
            //  2. User clicks "reconnect" button, which forces a page reload. Based on Sentry logs
            //     there's a time window where we report connection drop right before the entire
            //     tab is reloaded
            //  3. Sometimes there's a network glitch and we end up with a disconnected publisher
            //     even when the session is presumably online and we are connected. When this
            //     happens, let's attempt to reconnect.
            //
            // It is condition (3) that this magic is intended to respond to, while we should do
            // our best not to do anything in cases (1) & (2). Let's give it a 0.5sec window before
            // doing anything
            setTimeout(() => {
              Sentry.captureMessage(`ONLINE video session just LOST its publisher - ${evt.reason}`);
              if (isAlive()) {
                destroyPublisher();
                initCallPublisher(retryCount + 1);
              }
            }, 500);
          }
        } else {
          stopLocalRecording();
        }
      });

      publisher.on("videoElementCreated", (evt) => {
        trace(`CALL::Publisher(${publisher.shortId}) - videoElementCreated`);
        setTimeout(() => {
          dispatch({ type: CALL_PUBLISH_SUCCESS, publisher: evt.element });
        }, 0);
      });

      connectPublisher();
    }

    function initStreamSubscriber(stream) {
      const subscribeId = nanoid(8);
      const subscriber = callCtx.session.subscribe(stream, null, { insertDefaultUI: false }, (err) => {
        trace(`CALL::Session(${callCtx.session.shortId}) - SUBSCRIBE(${subscribeId}) ${err ? "FAIL" : "SUCCESS"}`);
        if (err) {
          reportError(logHeaders.SUBSCRIBE_ERROR, err);
        }
      });

      subscriber.shortId = subscribeId;
      callCtx.subscribers.set(stream.streamId, subscriber);

      subscriber.on("videoElementCreated", (subEvt) => {
        trace(`CALL::Subscriber(${subscriber.shortId}) - videoElementCreated`);
        const {
          call: { audioOutput },
        } = getState();

        if (audioOutput && stream.videoType !== "screen" && subEvt.element.setSinkId !== undefined) {
          subEvt.element.setSinkId(audioOutput);
        }

        let name;
        let sourceType;
        let userId;
        let visitorId;
        const connData = stream?.connection?.data;
        if (connData) {
          const parsed = new URLSearchParams(connData);
          if (parsed) {
            name = parsed.get("name");
            sourceType = parsed.get("source");
            userId = parsed.get("user_id");
            visitorId = parsed.get("visitor_id");
          }
        }
        dispatch({
          type: CALL_PEER_JOINED,
          id: stream.streamId,
          source: subEvt.element,
          audioEnabled: stream.hasAudio,
          videoEnabled: stream.hasVideo,
          isScreen: stream.videoType === "screen",
          name,
          source_type: sourceType,
          user_id: userId,
          visitor_id: visitorId,
        });
      });

      startSpeakerDetection(subscriber, () => {
        dispatch({ type: CALL_PEER_SPEAKING, peerID: stream.streamId });
      });

      const addEventHandler = (eventName: string, cb?: (evt?: any) => void) => {
        subscriber.on(eventName, (evt: any) => {
          trace(`CALL::Subscriber(${subscriber.shortId}) - ${eventName}`);
          if (cb) cb(evt);
        });
      };

      const dispatchTogglePeerVideo = (enabled: boolean) => () =>
        dispatch({
          type: CALL_TOGGLE_PEER_VIDEO,
          peerID: stream.streamId,
          enabled,
        });

      addEventHandler("audioBlocked");
      addEventHandler("audioUnblocked");
      addEventHandler("connected");
      addEventHandler("disconnected");
      addEventHandler("destroyed");
      addEventHandler("videoDisabled", dispatchTogglePeerVideo(false));
      addEventHandler("videoDisableWarning");
      addEventHandler("videoDisableWarningLifted");
      addEventHandler("videoEnabled", dispatchTogglePeerVideo(true));
    }

    function initCallSession() {
      const { openTokApiKey, sessionId } = callCtx.storyContext;
      const session = OT.initSession(openTokApiKey, sessionId);
      const sessionTraceId = nanoid(8);

      session.shortId = sessionTraceId;
      trace(`CALL::Session(${session.shortId}) - CREATED - SessionId=${session.sessionId}`);
      gtmTrace("call_session_init");

      callCtx.session = session;

      // OpenTok will reuse session objects if sessionId is the same. If the session is reused, we
      // want to clean up here and NOT during cleanup, so that we still capture all the cleanup events
      session.off();

      session.on("connectionCreated", (evt) => {
        const { connection } = evt;
        trace(`CALL::Session(${session.shortId}) - connectionCreated(${connection.connectionId}): ${connection.data}`);
        callCtx.localTimeOffset = evt.connection.creationTime ? new Date().getTime() - evt.connection.creationTime : 0;
      });

      session.on("connectionDestroyed", (evt) => {
        const { connection } = evt;
        trace(`CALL::Session(${session.shortId}) - connectionDestroyed(${connection.connectionId}): ${evt.reason}`);
      });

      session.on("sessionConnected", () => {
        trace(`CALL::Session(${session.shortId}) - sessionConnected`);
      });

      session.on("sessionDisconnected", (evt) => {
        if (sessionTraceId !== callCtx?.session?.shortId || !isOnline(getState().call.status)) return;

        trace(`CALL::Session(${session.shortId}) - sessionDisconnected: ${evt.reason}`);
        dispatch({ type: CALL_DISCONNECTED });
        destroyPublisher();
        connectSession(1);
        setTimeout(() => {
          Sentry.captureMessage(`ONLINE video session just LOST its connection: ${evt.reason}`);
        }, 500);
      });

      session.on("sessionReconnected", () => {
        trace(`CALL::Session(${session.shortId}) - sessionReconnected`);
      });

      session.on("sessionReconnecting", () => {
        // This event and the one above seems to be just added to their documentation and I am not
        // even sure how this works, if it works and when it works. For now just adding logging and
        // alerting so we can observe it in the while and then we'll go from there
        // -- DXM 2022-01-11
        trace(`CALL::Session(${session.shortId}) - sessionReconnecting`);

        // Wrap in a timeout so that we don't report the alert if the browser tab is
        // in the process of being killed
        setTimeout(() => {
          Sentry.captureMessage("ONLINE video session just LOST its connection (autoreconnect?)");
        }, 500);
      });

      session.on("archiveStarted", () => {
        trace(`CALL::Session(${session.shortId}) - archiveStarted`);
        // If we're on an existing call, this will probably start the local recorder
        // But if it's a new call, the call state's `.recording` won't be set yet, so this will
        // fail on the `canStartLocalRecording()` check
        void startLocalRecording();

        // Soooo... we dispatch this, which sets the call's `.recording: true`, so that later
        // when we get to `"streamCreated"`, we'll start it
        dispatch({ type: CALL_STARTED_RECORDING });
      });

      session.on("archiveStopped", () => {
        trace(`CALL::Session(${session.shortId}) - archiveStopped`);
        dispatch({ type: CALL_STOPPED_RECORDING, payload: { source: "event" } });
      });

      session.on("streamCreated", (evt) => {
        const { stream } = evt;
        trace(`CALL::Session(${session.shortId}) - streamCreated (${stream.streamId}); subscribing...`);
        initStreamSubscriber(stream);
      });

      session.on("streamDestroyed", (evt) => {
        const { stream } = evt;
        trace(`CALL::Session(${session.shortId}) - streamDestroyed (${stream.streamId}): ${evt.reason}`);
        callCtx.subscribers.delete(stream.streamId);
        dispatch({ type: CALL_PEER_LEFT, id: stream.streamId });
      });

      session.on("signal:msg", (event) => {
        const isMine = event.from.connectionId === session.connection.connectionId;
        dispatch({
          type: CALL_RECEIVED_MESSAGE,
          peerID: event.from.id,
          sender: event.data.sender,
          newMessage: event.data.message,
          isMine: isMine,
        });
      });

      session.on("signal:info", (event) => {
        const isMine = event.from.connectionId === session.connection.connectionId;
        dispatch({
          type: CALL_RECEIVED_INFO_MESSAGE,
          peerID: event.from.id,
          sender: event.data.sender,
          typing: event.data.typing,
          isMine: isMine,
        });
      });

      session.on("signal:hist", (event) => {
        const isMine = event.from.connectionId === session.connection.connectionId;
        const parsedChatHist = new Map(JSON.parse(event.data));
        dispatch({
          type: CALL_RECEIVED_CHAT_HISTORY_MESSAGE,
          chatHistory: parsedChatHist,
          isMine: isMine,
          receivingPeer: session.connection.connectionId,
        });
      });

      session.on("signal:peer", (event) => {
        console.log(`CALL::Session(${session.shortId}) - Signal.Peer ==> `, event.data);
        const eventData = JSON.parse(event.data);
        const payload = Array.isArray(eventData) ? eventData : [eventData];
        if (event.from.connectionId !== session.connection.connectionId) {
          dispatch(updateParticipantsLocal(payload));
        }
      });

      session.on("signal:requestpeer", (event) => {
        console.log(`CALL::Session(${session.shortId}) - Signal.RequestPeer`);
        const {
          call: { participants },
          auth: { visitorId },
        } = getState();
        const myInfo = participants.get(visitorId);
        if (myInfo && event.from.connectionId !== session.connection.connectionId) {
          const payload = { visitorId, data: myInfo, overwrite: true };
          sendPeerInfo(payload, event.from.connection);
        }
      });

      session.on("signal:recording_stopped", () => {
        dispatch({ type: CALL_STOPPED_RECORDING, payload: { source: "signal" } });
      });

      connectSession();
    }

    function connectSession(retryCount = 0) {
      const {
        session,
        storyContext: { token },
      } = callCtx;
      const sessionTraceId = session?.shortId;
      const isAlive = () => callCtx?.session?.shortId === sessionTraceId && isOnline(getState().call.status);

      session.connect(token, (error) => {
        // we only respond to the event if call context hasn't been reset (i.e. with a disconnect)
        if (!isAlive()) return;

        trace(`CALL::Session(${session.shortId}) - CONNECT ${error ? "FAIL" : "SUCCESS"}`);
        if (error) {
          reportError(logHeaders.CONNECT_ERROR, error, retryCount);
          scheduleRetry(retryCount, () => {
            if (isAlive()) connectSession(retryCount + 1);
          });
        } else {
          initCallPublisher();
        }
      });
    }

    function switchCallDevice(audioSource, videoSource, audioOutput) {
      if (audioOutput) {
        (callCtx.subscribers || []).forEach((subscriber) => {
          subscriber.videoElement().setSinkId(audioOutput);
        });
      }

      if (callCtx.publisher) {
        if (videoSource) {
          callCtx.publisher.setVideoSource(videoSource);
        }
        if (audioSource) {
          callCtx.publisher.setAudioSource(audioSource);
        }
        if (getState().call.recording && (videoSource || audioSource)) {
          stopLocalRecording();
          void startLocalRecording();
        }
      }

      dispatch({ type: CALL_SETTINGS_UPDATE_AUDIO_VIDEO, payload: { audioSource, videoSource, audioOutput } });

      // This function may be called with partial update (i.e. not all params passed in). However, the above
      // dispatch call is synchronous so at this point the redux call state has a complete picture that we can
      // save off in local storage
      const { call } = getState();
      localStorage.setItem(
        "userSavedCallDevices",
        JSON.stringify({
          audioinput: call.audioSource,
          videoinput: call.videoSource,
          audiooutput: call.audioOutput,
        })
      );
    }

    async function identifyAudioVideoDevices() {
      // We need to call getUserMedia to ensure that browser is allowing access to camera/microphone.
      // Without calling this, enumerateDevices() will not return anything useful
      const tempStream = await OT.getUserMedia();
      tempStream.getTracks().forEach((track) => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      let savedUserDevices = {};
      try {
        savedUserDevices = JSON.parse(localStorage.getItem("userSavedCallDevices") || "{}");
      } catch (err) {
        Sentry.captureException(err);
      }

      const [audioSource, videoSource, audioOutput] = ["audioinput", "videoinput", "audiooutput"].map((kind) => {
        if (kind === "audiooutput" && (HTMLMediaElement.prototype as any).setSinkId === undefined) {
          console.log("Current browser doesn't support setting audio output device");
          return null;
        }
        const deviceList = devices.filter((device) => device.kind === kind);
        return (
          deviceList.find((d) => d.deviceId === savedUserDevices[kind])?.deviceId ||
          deviceList.find(
            (d) => d.deviceId.toLowerCase().includes("default") || d.label.toLowerCase().includes("default")
          )?.deviceId ||
          (deviceList.length && deviceList[0].deviceId) ||
          null
        );
      });

      const payload = { audioSource, videoSource, audioOutput };

      if (audioSource == null || videoSource == null) {
        console.log("enumerateDevices() returned:\n", JSON.stringify(devices, null, 2));
        Sentry.captureMessage("identifyAudioVideoDevices() did not find mic/camera!!");
      }

      trace(`CALL::Media - Identify: speaker(${audioOutput}), mic(${audioSource}), cam(${videoSource})`);

      dispatch({ type: CALL_SETTINGS_UPDATE_AUDIO_VIDEO, payload });
    }

    function initializeCall(data) {
      callCtx.storyContext = data;
      if (!data.dolbyConference) {
        identifyAudioVideoDevices()
          .then(() => {
            // If the user hits "hang up" while we are checking a/v devices, let's
            // make sure we don't proceed with connecting to a call while the app
            // is already displaying the home page
            if (getState().call.status === STATUS_CONNECTING) {
              initCallSession();
            }
          })
          .catch((err) => {
            dispatch({ type: CALL_FAILED, error: err });
            console.log(err);
            Sentry.captureException(err);
          });
      }
    }

    function connectPublisher(retryCount = 0) {
      const { session, publisher } = callCtx;
      const isAlive = () => publisher === callCtx.publisher && isOnline(getState().call.status);

      trace(`CALL::Session(${session.shortId}) - Publishing(${publisher.shortId})...`);

      session.publish(publisher, (err: Error) => {
        if (!isAlive()) return;

        trace(`CALL::Session(${session.shortId}) - Publish(${publisher.shortId}) ${err ? "FAIL" : "SUCCESS"}`);
        if (err) {
          reportError(logHeaders.PUBLISH_ERROR, err, retryCount);
          scheduleRetry(retryCount, () => {
            if (isAlive()) connectPublisher(retryCount + 1);
          });
        } else {
          dispatch({ type: CALL_STARTED, data: callCtx.storyContext });
          dispatch({ type: CALL_LOCAL_RECORDING_CAPABILITY_DETERMINED, data: callCtx.localRecordingCapability });
        }
      });
    }

    function disconnectPublisher(publisher: any) {
      const session = publisher.session;
      if (session) {
        trace(`CALL::Session(${session.shortId}) - Unpublishing (${publisher.shortId})...`);
        try {
          session.unpublish(publisher);
        } catch (err) {
          // At this point we are doing cleanup. If anything fails, can't do much about it
        }
      }
    }

    function destroyPublisher() {
      const { publisher } = callCtx;
      if (!publisher) return;

      // We clear the publisher from the context first so that if cleanup triggers any
      // event handlers, those will silent exit because isAlive check will return false
      callCtx.publisher = null;

      disconnectPublisher(publisher);

      trace(`CALL::Publisher(${publisher.shortId}) - Destroying...`);

      try {
        publisher.destroy();
      } catch (err) {
        // At this point we are doing cleanup. If anything fails, can't do much about it
      }
    }

    function terminateCall() {
      gtmTrace("call_terminate");

      const { session } = callCtx;

      // We clear the publisher from the context first so that if cleanup triggers any
      // event handlers, those will silent exit because isAlive check will return false
      callCtx.session = null;
      callCtx.subscribers.clear();

      destroyPublisher();

      try {
        trace(`CALL::Session(${session.shortId}) - Disconnecting...`);
        session.disconnect();
      } catch (err) {
        // TODO: Remove these?  These errors might be normal and unavoidable. For now
        // leaving them in just so that we can observe how often they happen if we need
        // to do something about them. But as this is all part of the synchronous clean up
        // path, at this point ain't much we can do regardless of what opentok client does
        // -- DXM 2022-01-12
        console.error("Error disconnecting session: ", err);
        Sentry.captureException(err);
      }
    }

    function toggleAudio(callState) {
      gtmTrace("call_toggle_audio");
      const isEnabled = callState.publishing.audio;
      callCtx.localMediaRecorder?.stream?.getAudioTracks().forEach((track: MediaStreamTrack) => {
        track.enabled = isEnabled;
      });
      const { publisher } = callCtx;
      if (publisher) {
        publisher.publishAudio(isEnabled);
        trace(`CALL::Publisher(${publisher.shortId}) - publishAudio=${isEnabled}`);
      }
    }

    function toggleVideo(callState) {
      gtmTrace("call_toggle_video");
      const isEnabled = callState.publishing.video;
      callCtx.localMediaRecorder?.stream?.getVideoTracks().forEach((track: MediaStreamTrack) => {
        track.enabled = isEnabled;
      });
      const { publisher } = callCtx;
      if (publisher) {
        publisher.publishVideo(isEnabled);
        trace(`CALL::Publisher(${publisher.shortId}) - publishVideo=${isEnabled}`);
      }
    }

    function handleStoppedRecording(source: string) {
      if (source === "user") {
        // This logic here is purely because once in a while tokbox API will have a hiccup
        // and when we attempt to start recording, they will return HTTP 500. When that
        // happens they DO stop the recording, but they never send "archiveStopped" Session
        // event and so our UI continues to show the user that recording is in progress
        // which confuses the hell out of our users. To work around that, when the host
        // stops the recording, we issue (and catch) this signal and yeah... in normal flow
        // that means we end up dispatching CALL_STOPPED_RECORDING three times (from event,
        // from the user and then from the signal). Reducer will mark recording stopped 3
        // times. Alternative is to introduce more event types, which we could also do
        sessionSignal({ type: "recording_stopped" });
      } else {
        stopLocalRecording();
      }
    }

    function togglePeerAudio(peerId, callState) {
      gtmTrace("call_toggle_peer_audio");
      const { subscribers } = callCtx;
      const subscriber = subscribers.get(peerId);
      const peer = callState.peers.get(peerId);
      if (subscriber && peer) {
        subscriber.subscribeToAudio(peer.audioEnabled);
        trace(`CALL::Subscriber(${subscriber.shortId}) - subscibeToAudio=${peer.audioEnabled}`);
      }
    }

    function setPeerResolution(peerId, newResolution) {
      const { subscribers } = callCtx;
      const subscriber = subscribers.get(peerId);
      if (subscriber) {
        subscriber.setPreferredResolution(newResolution);
      }
    }

    function setPeerFrameRate(peerId, newFrameRate) {
      const { subscribers } = callCtx;
      const subscriber = subscribers.get(peerId);
      if (subscriber) {
        subscriber.setPreferredFrameRate(newFrameRate);
      }
    }

    function muteAll(callState) {
      const { subscribers } = callCtx;
      callState.peers.forEach((peer, id) => {
        const subscriber = subscribers.get(id);
        if (subscriber) {
          subscriber.subscribeToAudio(false);
        }
      });
    }

    function checkScreenShare() {
      OT.checkScreenSharingCapability((res) => {
        dispatch({ type: CALL_SCREEN_SHARE_CAP, supported: res.supported });
      });
    }

    function startScreenSharing() {
      OT.checkScreenSharingCapability((res) => {
        if (res.supported) {
          if (res.extensionRequired && !res.extensionInstalled) {
            dispatch({ type: CALL_SCREEN_SHARE_EXT, visible: true });
          } else {
            const publisher = OT.initPublisher(
              null,
              {
                videoSource: "screen",
                insertDefaultUI: false,
              },
              (err) => err && reportError(logHeaders.INIT_PUBLISH_SCREEN_ERROR, err)
            );

            publisher.on("videoElementCreated", (evt) => {
              setTimeout(() => {
                dispatch({
                  type: CALL_PEER_JOINED,
                  id: "my-screen",
                  source: evt.element,
                  audioEnabled: false,
                  videoEnabled: true,
                  isScreen: true,
                  name: "Yourself",
                  source_type: "browser",
                });
              }, 0);
            });

            publisher.on("mediaStopped", () => {
              dispatch({ type: CALL_SCREEN_SHARING, sharing: false });
              dispatch({ type: CALL_PEER_LEFT, id: "my-screen" });
            });

            publisher.on("streamDestroyed", (evt) => {
              evt.preventDefault();
            });

            callCtx.session.publish(publisher, (error) => {
              if (error) {
                reportError(logHeaders.SCREEN_SHARE_ERROR, error);
              } else {
                callCtx.screenShare = publisher;
                dispatch({ type: CALL_SCREEN_SHARING, sharing: true });
              }
            });
          }
        }
      });
    }

    function stopScreenSharing() {
      dispatch({ type: CALL_PEER_LEFT, id: "my-screen" });
      if (callCtx.screenShare) {
        try {
          callCtx.session.unpublish(callCtx.screenShare);
          callCtx.screenShare.destroy();
        } catch (err) {
          Sentry.captureException(err);
          console.error(err);
        }
        callCtx.screenShare = null;
      }
      dispatch({ type: CALL_SCREEN_SHARING, sharing: false });
    }

    function sessionSignal(payload: any, onSuccess?: () => void) {
      // If something here attempts to send a signal before session is connected, let's avoid
      // generating any extra errors and simply do nothing
      if (callCtx.session?.connection) {
        callCtx.session.signal(payload, (err) => {
          if (err) {
            console.error("Error sending session signal:", err.name, err.message);
            Sentry.captureException(err);
          } else if (onSuccess) {
            onSuccess();
          }
        });
      }
    }

    function sendMessage(newMessage, sender) {
      sessionSignal(
        {
          type: "msg",
          data: { sender: sender, message: newMessage },
        },
        () => {
          dispatch({ type: CALL_SENT_MESSAGE, newSentMessage: newMessage });
        }
      );
    }

    function sendTyping(sender, typing) {
      sessionSignal({
        type: "info",
        data: { sender: sender, typing: typing },
      });
    }

    function sendChatHistory(chatHist) {
      sessionSignal({
        type: "hist",
        data: JSON.stringify([...chatHist]),
      });
    }

    function identifyParticipant(name: string) {
      dispatch(broadcastParticipantUpdate(getState().auth.visitorId, { name }));
    }

    function sendPeerInfo(payload: any, to?: string) {
      sessionSignal({
        ...(to ? { to } : {}),
        type: "peer",
        data: JSON.stringify(Array.isArray(payload) ? payload : [payload]),
      });
    }

    function requestPeerInfo() {
      sessionSignal({ type: "requestpeer" });
    }

    function handleStoredLocalRecordingFileUpload(
      stream: LocalAudioStream,
      eventType: "started" | "complete" | "failed" | "pending" | "info",
      extraUpdatePaload = {}
    ) {
      // The flow here is a bit convoluted, but here it is:
      // Local recording events get into redux by way of action dispatches:
      // - CALL_LOCAL_RECORDING_UPLOAD_PENDING -- Provides into about the upload progress of the currently uploading chunk
      // - CALL_LOCAL_RECORDING_UPLOAD_INFO -- Provides info about all of the chunks to upload, their size and upload status
      //
      // The following ought to be self-explanatory, and shouldn't include any extra metadata:
      // - CALL_LOCAL_RECORDING_UPLOAD_START
      // - CALL_LOCAL_RECORDING_UPLOAD_COMPLETION
      // - CALL_LOCAL_RECORDING_UPLOAD_FAILED
      // Two different reducers listen to these actions: call middleware (this file), and localRecording middleware.
      //
      // - When the call middleware sees these events, it routes all of them to this function,
      //   which after a little massaging, sends them off to update the local and remote peer info objects.
      //   In _this_ handler, we want to filter out events that don't pertain to the currently active call
      //   because other members of the call don't want or need to know about the status of your other local recordings.
      //
      // - When the localRecording middleware sees these events, it handles them irrespective of the currently active call.
      //   This way, we build up an aggregate of all local recordings the current user has in progress.
      //   Importantly, this data structure is never shared with other users/peers, so we don't have to worry about
      //   Inadvertently oversharing, and then filtering clientside etc etc.
      //
      // NOTE:
      // The peer info popup in the call page uses the codepath that _this_ function is a member of, i.e
      // it only sees local recording info about the currently active call.
      // On the other hand, the local recording status page uses the localRecording reducer, which is unfiltered
      // but only sees info for the current user.

      if (stream.sessionId !== callCtx.session.sessionId) return;
      // Our local recording signalling right now follows a state machine off...
      //   1. started -- file is being stored (and written to) on client machine.
      //   2. pending -- file is being uploaded -- this event includes progress information, so it'll get sent many times
      //   2a. info:  -- this doesn't indicate a state transition, but rather conveys information about the chunks for this recording
      //   3. complete/failed: the recording succeeded or failed to upload -- no more uploading
      // We might end up here because we found a file in local IDB, but the file isn't being uploaded yet because
      // of 60 sec window. In that case, event type will be "pending". We are going to signal to other participants
      // that the file stopped, which they will interpret "upload is in progress" when in fact it is not for
      // up to 60 sec.
      // Yeah... not ideal
      //
      // -- DXM 2021-09-01
      // -- Updated JAS 2022-02-08

      const updateDataMap: { [k in typeof eventType]: Partial<LocalRecordingInfo> } = {
        started: {
          localRecordingStarted: true,
          sessionId: stream.sessionId,
          ...extraUpdatePaload,
        },
        complete: {
          localRecordingUploadSucceeded: true,
          sessionId: stream.sessionId,
        },
        failed: {
          localRecordingUploadFailed: true,
          sessionId: stream.sessionId,
        },
        info: {
          sessionId: stream.sessionId,
          ...extraUpdatePaload,
        },
        pending: {
          localRecordingStarted: true,
          sessionId: stream.sessionId,
          ...extraUpdatePaload,
        },
      };

      dispatch(broadcastParticipantUpdate(getState().auth.visitorId, stream.id, updateDataMap[eventType]));
    }

    function handleUserAuthUpdate() {
      const {
        call: { participants },
        auth: { visitorId, full_name: authName },
      } = getState();
      const myInfo = participants.get(visitorId);
      if (myInfo && authName && myInfo.name !== authName) {
        dispatch(broadcastParticipantUpdate(visitorId, { name: authName }));
      }
    }

    function handleToggleLocalRecording() {
      // This method is here only to help us diagnose #810, and once we are done with that, we'd
      // most likely want to get rid off this... maybe
      trace(`TOGGLE(Local Recording): ${getState().call.localRecording}`);
    }

    return (next) => (action: any) => {
      // Let's make sure Redux state reflects reality before this middleware performs any of its
      // own side-effects.
      next(action);

      const { call } = getState();
      const callNotRequired = [CALL_INIT, CALL_CHECK_SCREEN_SHARE, CALL_PARTICIPANT_IDENTIFIED, AUTH_USER];

      if (callCtx.session || callNotRequired.includes(action.type)) {
        switch (action.type) {
          case CALL_INIT:
            initializeCall(action.payload);
            break;
          case CALL_FINISH:
            terminateCall();
            break;
          case CALL_STOP_SETTINGS:
            stopCallSettings();
            break;
          case CALL_START_SETTINGS:
            initializeSettings(action.payload);
            break;
          case CALL_SETTINGS_PUBLISH:
            createSettingsPublisher(action.audioSource, action.videoSource);
            break;
          case CALL_SETTINGS_SWITCH_DEVICE:
            switchSettingsDevice(action.audioSource, action.videoSource);
            break;
          case CALL_SWITCH_DEVICE:
            switchCallDevice(action.audioSource, action.videoSource, action.audioOutput);
            break;
          case CALL_MUTE_ALL:
            muteAll(call);
            break;
          case CALL_TOGGLE_MICROPHONE:
            toggleAudio(call);
            break;
          case CALL_TOGGLE_CAMERA:
            toggleVideo(call);
            break;
          case CALL_STOPPED_RECORDING:
            handleStoppedRecording(action.payload.source);
            break;
          case CALL_TOGGLE_PEER_AUDIO:
            togglePeerAudio(action.peerID, call);
            break;
          case CALL_SET_PEER_RESOLUTION:
            setPeerResolution(action.peerID, action.newResolution);
            break;
          case CALL_SET_PEER_FRAME_RATE:
            setPeerFrameRate(action.peerID, action.newFrameRate);
            break;
          case CALL_CHECK_SCREEN_SHARE:
            checkScreenShare();
            break;
          case CALL_START_SCREEN_SHARING:
            startScreenSharing();
            break;
          case CALL_STOP_SCREEN_SHARING:
            stopScreenSharing();
            break;
          case CALL_SEND_MESSAGE:
            sendMessage(action.newMessage, action.sender);
            break;
          case CALL_SEND_PEER_INFO:
            sendPeerInfo(action.payload);
            break;
          case CALL_REQUEST_PEER_INFO:
            requestPeerInfo();
            break;
          case CALL_SEND_INFO_MESSAGE:
            sendTyping(action.sender, action.typing);
            break;
          case CALL_SEND_CHAT_HISTORY_MESSAGE:
            sendChatHistory(action.historyChat);
            break;
          case CALL_FLUSH_LOCAL_RECORDING:
            flushLocalRecording();
            break;
          case CALL_PARTICIPANT_IDENTIFIED:
            identifyParticipant(action.payload);
            break;
          case CALL_LOCAL_RECORDING_UPLOAD_PENDING:
            if (action.data.partsUploaded != null) {
              // Only called by non-streaming uploader as the way to indicate upload progress
              handleStoredLocalRecordingFileUpload(action.data.stream, "pending", {
                partsUploaded: action.data.partsUploaded,
                partBytes: action.data.partBytes,
                uploadedBytes: action.data.uploadedBytes,
              });
            }
            break;
          case CALL_LOCAL_RECORDING_UPLOAD_START:
            handleStoredLocalRecordingFileUpload(action.data.stream, "started");
            break;
          case CALL_LOCAL_RECORDING_UPLOAD_COMPLETION:
            handleStoredLocalRecordingFileUpload(action.data.stream, "complete");
            break;
          case CALL_LOCAL_RECORDING_UPLOAD_FAILED:
            handleStoredLocalRecordingFileUpload(action.data.stream, "failed");
            break;
          case AUTH_USER:
            handleUserAuthUpdate();
            break;
          case CALL_TOGGLE_LOCAL_RECORDING:
            handleToggleLocalRecording();
            break;
          default:
        }
      }
    };
  };
